import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Backdrop, CircularProgress, Dialog, DialogContent, DialogProps, Grid, Theme, ButtonBase } from '@mui/material';
import PageBreadCrumbs from '../common/PageBreadCrumbs';
import { FileForm } from '../../models/inputTypes/FileType';
import {
  performGetFrame,
  performGetSrfHeatmap,
  performReplaceFile,
  performGetPoints,
  resetEditFile,
  selectEditFileError,
  selectEditFileFrame,
  selectEditFileHeatmapSrf,
  selectEditFileLoading,
  selectEditFileClicks,
  selectEditFileReplaced,
  setLoadingEditFile,
  setReplacedFile,
} from '../../redux/slices/editFileSlice';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { HotKeys } from 'react-hotkeys';
import FileSaveCard from '../groups/srf/heatmapModal/FileSaveCard';
import FileNameCard from '../groups/heatMapModal/FileNameCard';
import FileHeatmapCard from '../groups/srf/heatmapModal/FileHeatmapCard';
import FileTimeframeCard from '../groups/heatMapModal/FileTimeFrameCard';
import { useGetProjectsQuery } from '../../redux/RTK/queries/projectQuery';
import { ReactComponent as Close } from '../../assets/images/close.svg';
import { openWarningToast } from '../../redux/slices/appSlice';
import { putReplaceFile } from '../../axios/endpoints/editFile';

const styles = {
  dialog: {
    overflowY: 'visible',
    height: '100%',
    maxHeight: 'calc(100% - 16px)',
  },
  closeButton: {
    position: 'absolute',
    right: -10,
    top: -5,
    boxShadow: '0px 0px 5px 1px gray',
    zIndex: '200000 !important',
    borderRadius: '50%',
    width: '35px',
    padding: 0,
    minWidth: 0,
    color: (theme: Theme) => theme.palette.info.dark,
    background: 'white',
    height: '35px',
  },
  gridContainer: {
    minHeight: '100%',
  },
  backdropContainer: {
    position: 'relative',
  },
  backdrop: {
    position: 'absolute',
    zIndex: (theme: Theme) => theme.zIndex.drawer + 1,
    color: (theme: Theme) => theme.palette.common.white,
    borderRadius: 1,
    width: '100%',
    height: '100%',
  },
} as const;

interface CustomHeatmapModalProps {
  handleClose: () => void;
  file?: FileForm;
  open: boolean;
}

const HeatmapSrfModal: FC<CustomHeatmapModalProps & Exclude<DialogProps, 'open'>> = ({
  open,
  file,
  handleClose,
  ...props
}) => {
  const dispatch = useAppDispatch();
  const fileName = useMemo(() => file?.name || '', [file]);
  const projectId = useMemo(() => file?.project.toString() || '', [file]);
  const { data: projects } = useGetProjectsQuery({});

  const [frameCoords, setFrameCoords] = useState<{ x: number; y: number }>({ x: -1, y: -1 });
  const [value, setValue] = useState<number | undefined>(undefined);
  const [checkValue, setCheckValue] = useState<number | undefined>(10);
  const [timeframe, setTimeframe] = useState<number>(1);
  const [grid, setGrid] = useState<any[][]>();
  const [gridEditHistory, setGridEditHistory] = useState<any[][][]>([]);
  const [draw, setDraw] = useState<boolean>(false);
  const [points, setPoints] = useState<{ x: number; y: number }[]>([]);
  const [clicks, setClicks] = useState<{ x: number; y: number }[]>([]);
  const [allPoints, setAllPoints] = useState<{ x: number; y: number }[] | undefined>([]);
  const [selected, setSelect] = useState<boolean>(false);
  const [changedPoints, setChangedPoints] = useState<{ x: number; y: number }[] | undefined>([]);
  const [saveName, setSaveName] = useState<string | undefined>(fileName);
  const [autoSave, setAutoSave] = useState<boolean>(false);

  const project = useMemo(() => projects?.find((item) => item.id === +projectId), [projects]);

  const heatmap = useAppSelector(selectEditFileHeatmapSrf);
  const frame = useAppSelector(selectEditFileFrame);
  const editFileError = useAppSelector(selectEditFileError);
  const editFileLoading = useAppSelector(selectEditFileLoading);
  const selectedPoints = useAppSelector(selectEditFileClicks);
  const editFileSaved = useAppSelector(selectEditFileReplaced);

  useEffect(() => {
    if (selected) {
      dispatch(performGetPoints(projectId, fileName, timeframe, clicks, checkValue));
      setClicks([]);
      setDraw(false);
      setSelect(false);
    }
  }, [selected]);

  const beforeUnload = useCallback(
    (event) => {
      if (gridEditHistory.length > 0) {
        event.preventDefault();
        if (autoSave) {
          handleAutoSave();
        }
        const confirmationMessage = 'Changes that you made may not be saved.';
        event.returnValue = confirmationMessage;
        return confirmationMessage;
      }
    },
    [gridEditHistory, autoSave],
  );

  const undoShortcut = useCallback(() => {
    const gridHistory = gridEditHistory.slice();
    if (gridHistory.length > 0) {
      gridHistory.pop();
      setGridEditHistory(gridHistory);
      if (gridHistory.length > 0) {
        setGrid(gridHistory[gridHistory.length - 1]);
      } else {
        setGrid(frame.points);
      }
    }
  }, [gridEditHistory, frame]);

  useEffect(() => {
    window.addEventListener('beforeunload', beforeUnload);

    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [beforeUnload, gridEditHistory, autoSave]);

  useEffect(() => {
    return () => {
      dispatch(resetEditFile());
    };
  }, []);

  useEffect(() => {
    if (heatmap) {
      if (frameCoords.x == -1 || frameCoords.y === -1) {
        setFrameCoords({ x: 0, y: heatmap.dimensions?.[0] });
        dispatch(performGetFrame(projectId, fileName, timeframe, 0, heatmap.dimensions?.[0]));
      }
    }
  }, [heatmap]);

  useEffect(() => {
    setFrameCoords({ x: -1, y: -1 });
    setGrid(undefined);
    dispatch(performGetSrfHeatmap(projectId, fileName, timeframe));
    setAllPoints([]);
  }, [timeframe]);

  useEffect(() => {
    if (frameCoords.x >= 0 && frameCoords.y >= 0) {
      dispatch(performGetFrame(projectId, fileName, timeframe, frameCoords?.x, frameCoords?.y));
      if (gridEditHistory.length > 0) {
        dispatch(performGetSrfHeatmap(projectId, fileName, timeframe));
      }
    }
    setChangedPoints([]);
  }, [frameCoords]);

  useEffect(() => {
    if (!draw) {
      setPoints([]);
    }
  }, [draw]);

  useEffect(() => {
    if (frame) {
      setGridEditHistory([]);
      setGrid(frame.points);
    }
  }, [frame]);

  useEffect(() => {
    if (selectedPoints) {
      if (selectedPoints.length === 0) {
        dispatch(openWarningToast('No matching points found.'));
      }
      selectedPoints?.forEach((item) => {
        setPoints((prevPoints) => [...(prevPoints as { x: number; y: number }[]), { x: item.x, y: item.y }]);
      });
    }
  }, [selectedPoints]);

  useEffect(() => {
    if (editFileSaved?.destinationFile) {
      dispatch(setReplacedFile(undefined));
      handleClose();
    } else if (editFileSaved?.fileName) {
      const projectid = `${editFileSaved.projectId}`;
      const filename = `${editFileSaved.fileName}`;
      if (value) {
        points?.forEach((item) => {
          setAllPoints((prevPoints) => [...(prevPoints as { x: number; y: number }[]), { x: item.x, y: item.y }]);
        });
      }
      changedPoints?.forEach((item) => {
        setAllPoints((prevPoints) => [...(prevPoints as { x: number; y: number }[]), { x: item.x, y: item.y }]);
      });
      dispatch(setReplacedFile(undefined));
      setPoints([]);
      setChangedPoints([]);
      if (projectId === projectid && fileName === filename) {
        dispatch(performGetSrfHeatmap(projectId, fileName, timeframe));
      }
    }
  }, [editFileSaved]);

  const handleSave = async () => {
    await dispatch(
      performReplaceFile(
        projectId,
        fileName,
        saveName !== fileName ? saveName : undefined,
        timeframe,
        points,
        value,
        grid,
        frameCoords?.x,
        frameCoords?.y,
      ),
    );
    await dispatch(performGetFrame(projectId, fileName, timeframe, frameCoords?.x, frameCoords?.y));
  };

  const handleAutoSave = (nextCoords?: { x: number; y: number } | undefined, nextTimeframe?: number) => {
    if (editFileLoading.replace) return;
    dispatch(setLoadingEditFile({ key: 'replace', value: true }));
    dispatch(setLoadingEditFile({ key: 'heatmapSrf', value: true }));
    if (nextCoords || nextTimeframe) {
      dispatch(setLoadingEditFile({ key: 'frame', value: true }));
    }
    putReplaceFile(
      projectId,
      fileName,
      undefined,
      timeframe,
      undefined,
      undefined,
      grid,
      frameCoords?.x,
      frameCoords?.y,
    )
      .then((r) => {
        setGridEditHistory([]);
        changedPoints?.forEach((item) => {
          setAllPoints((prevPoints) => [...(prevPoints as { x: number; y: number }[]), { x: item.x, y: item.y }]);
        });
        setChangedPoints([]);
        dispatch(setLoadingEditFile({ key: 'replace', value: false }));
        if (nextTimeframe) {
          setTimeframe(nextTimeframe);
        } else {
          dispatch(performGetSrfHeatmap(projectId, fileName, timeframe));
        }
        if (nextCoords) {
          setFrameCoords(nextCoords);
        }
      })
      .catch((e) => {
        dispatch(setLoadingEditFile({ key: 'replace', value: false }));
        dispatch(setLoadingEditFile({ key: 'heatmapSrf', value: false }));
        if (nextCoords || nextTimeframe) {
          dispatch(setLoadingEditFile({ key: 'frame', value: false }));
        }
      });
  };

  const handleEditGrid = (values: any[][]) => {
    const gridHistory = gridEditHistory.slice();
    gridHistory.push(values);
    setGridEditHistory(gridHistory);
    setGrid(values);
  };

  const handleSetFrameCoords = (values: { x: number; y: number }) => {
    if (values.x >= 0 && values.y >= 0 && (frameCoords.x !== values?.x || frameCoords.y !== values?.y)) {
      if (autoSave && gridEditHistory.length > 0) {
        handleAutoSave(values);
      } else {
        setFrameCoords(values);
      }
    }
  };

  const handleSetTimeframe = (value: number) => {
    if (autoSave && gridEditHistory.length > 0) {
      handleAutoSave(undefined, value);
    } else {
      setTimeframe(value);
    }
  };

  const keyMap = {
    UNDO: ['command+z', 'ctrl+z'],
  };

  const handlers = {
    UNDO: undoShortcut,
  };

  const onDraw = () => {
    setDraw(!draw);
  };

  return (
    <Dialog
      maxWidth={false}
      PaperProps={{ style: styles.dialog }}
      fullWidth
      onClose={handleClose}
      open={open}
      disableEscapeKeyDown={draw}
      {...props}
    >
      <ButtonBase sx={styles.closeButton} onClick={handleClose}>
        <Close />
      </ButtonBase>
      <DialogContent>
        <HotKeys keyMap={keyMap} handlers={handlers} allowChanges>
          <Grid sx={styles.gridContainer} container spacing={2} justifyContent={'space-between'}>
            <Grid item xs={4} container spacing={2} alignContent={'flex-start'}>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12}>
                  <PageBreadCrumbs
                    path={[
                      'Projects',
                      project?.name?.toString() ?? file?.project.toString() ?? 'unavailable',
                      file?.name ?? 'unavailable',
                    ]}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FileNameCard
                    dimensions={heatmap?.dimensions}
                    projectName={project?.name?.toString() ?? file?.project.toString() ?? 'unavailable'}
                    fileName={fileName}
                  />
                </Grid>
              </Grid>
              <Grid item container xs={12} spacing={2}>
                <Grid item xs={12}>
                  <FileSaveCard
                    timeframe={heatmap?.totalTimeFrames}
                    value={value}
                    setValue={setValue}
                    checkValue={checkValue}
                    setCheckValue={setCheckValue}
                    saveName={saveName}
                    setSaveName={setSaveName}
                    onSave={handleSave}
                    selectLoading={editFileLoading.select}
                    saveLoading={editFileLoading.replace}
                    draw={draw}
                    onDraw={onDraw}
                    autoSave={autoSave}
                    setAutoSave={setAutoSave}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={8} container spacing={2} alignContent={'space-between'}>
              <Grid item xs={12}>
                <FileTimeframeCard
                  timeframe={timeframe}
                  setTimeframe={handleSetTimeframe}
                  totalTimeFrames={heatmap?.totalTimeFrames}
                />
              </Grid>
              <Grid item xs={12} container>
                <FileHeatmapCard
                  draw={draw}
                  clicks={clicks}
                  setClicks={setClicks}
                  points={points}
                  allPoints={allPoints}
                  changedPoints={changedPoints}
                  setChangedPoints={setChangedPoints}
                  errorHeat={!!editFileError.heatmapSrf}
                  errorGrid={!!editFileError.frame}
                  grid={grid}
                  setGrid={handleEditGrid}
                  gridLoading={editFileLoading.frame}
                  heatmap={heatmap}
                  frameCoords={frameCoords}
                  setFrameCoords={handleSetFrameCoords}
                  heatmapLoading={editFileLoading.heatmapSrf}
                  setSelect={setSelect}
                />
              </Grid>
            </Grid>
          </Grid>
        </HotKeys>
      </DialogContent>
      <Backdrop sx={styles.backdrop} open={editFileLoading.select}>
        <CircularProgress color={'inherit'} />
      </Backdrop>
    </Dialog>
  );
};

export default HeatmapSrfModal;
